import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { colours } from '../../theme';

/** A part of each month's bar: charger sessions, or home charging (Powersensor). */
export interface ChartSeries {
  key: string;
  label: string;
  /** From the design guidelines' palette. */
  color: string;
  /** kWh by month, "YYYY-MM". */
  values: Record<string, number>;
}

const SHORT = new Intl.DateTimeFormat('en-AU', { month: 'short', timeZone: 'UTC' });
const LONG = new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const at = (month: string) => new Date(`${month}-01T00:00:00Z`);
const kwh = (v: number) => (v >= 100 ? Math.round(v).toLocaleString('en-AU') : v.toFixed(1));

/** Gridline steps: 1, 2 or 5 times a power of ten, about 4 lines. */
function niceMax(max: number): { top: number; step: number } {
  if (max <= 0) return { top: 10, step: 2.5 };
  const rough = max / 4;
  const p = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * p).find((s) => s >= rough)!;
  return { top: Math.ceil(max / step) * step, step };
}

const PLOT_H = 200;
const GAP = 2; // surface gap between stacked parts

/**
 * Stacked columns, one per month (oldest on the left), each split into its parts. Hover or
 * tab to a month for its numbers. A hidden table carries the same figures for screen readers.
 */
export function MonthlyChargingChart({ months, series, loading }: { months: string[]; series: ChartSeries[]; loading?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  if (loading) return <ChartSkeleton />;

  const totals = months.map((m) => series.reduce((t, s) => t + (s.values[m] ?? 0), 0));
  const { top, step } = niceMax(Math.max(0, ...totals));
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const shown = series.filter((s) => months.some((m) => (s.values[m] ?? 0) > 0));
  const legend = shown.length > 1 ? shown : [];

  return (
    <Box>
      {legend.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 2 }} aria-hidden>
          {legend.map((s) => (
            <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: s.color }} />
              <Typography variant="body2" color="textSecondary">{s.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1 }} aria-hidden>
        {/* Y axis: kWh at each gridline. */}
        <Box sx={{ position: 'relative', width: 56, height: PLOT_H, flexShrink: 0 }}>
          {ticks.map((t) => (
            <Typography key={t} variant="caption" className="num" sx={{ position: 'absolute', right: 0, bottom: `${(t / top) * 100}%`, transform: 'translateY(50%)', color: colours.graphite, lineHeight: 1 }}>
              {t >= 1000 ? `${(t / 1000).toLocaleString('en-AU', { maximumFractionDigits: 1 })}k` : t.toLocaleString('en-AU')}
            </Typography>
          ))}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ position: 'relative', height: PLOT_H, display: 'flex', alignItems: 'flex-end', borderBottom: `1px solid ${colours.coolGrey}` }} onMouseLeave={() => setHover(null)}>
            {ticks.slice(1).map((t) => (
              <Box key={t} sx={{ position: 'absolute', left: 0, right: 0, bottom: `${(t / top) * 100}%`, borderTop: `1px solid ${colours.paleGrey}` }} />
            ))}
            {months.map((m, i) => {
              const parts = series.map((s) => ({ s, v: s.values[m] ?? 0 })).filter((p) => p.v > 0);
              return (
                <Box
                  key={m}
                  onMouseEnter={() => setHover(i)}
                  sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', position: 'relative', zIndex: 1, cursor: 'default' }}
                >
                  {/* Hover band: the whole column, wider than the bar. */}
                  {hover === i && <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(1,2,5,0.04)', borderRadius: '4px' }} />}
                  <Box sx={{ width: '60%', maxWidth: 24, display: 'flex', flexDirection: 'column-reverse', gap: `${GAP}px`, position: 'relative' }}>
                    {parts.map((p, j) => (
                      <Box
                        key={p.s.key}
                        sx={{
                          height: Math.max(2, (p.v / top) * PLOT_H - (j > 0 ? GAP : 0)),
                          bgcolor: p.s.color,
                          // Rounded only at the data end (the top of the stack), square at the baseline.
                          borderRadius: j === parts.length - 1 ? '4px 4px 0 0' : 0,
                        }}
                      />
                    ))}
                  </Box>
                  {hover === i && <Tip month={m} series={series} total={totals[i]!} side={i > months.length / 2 ? 'left' : 'right'} />}
                </Box>
              );
            })}
          </Box>
          {/* Months; the year under the first month and each January. */}
          <Box sx={{ display: 'flex', mt: 1 }}>
            {months.map((m, i) => (
              <Box key={m} sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', color: hover === i ? colours.nearBlack : colours.graphite, fontWeight: hover === i ? 600 : 400, lineHeight: 1.3 }}>
                  {SHORT.format(at(m))}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: colours.graphite, visibility: i === 0 || m.endsWith('-01') ? 'visible' : 'hidden', lineHeight: 1.3 }}>
                  {m.slice(0, 4)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* The same numbers as a table, for screen readers. */}
      <Box component="table" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
        <caption>Charging per month, kWh</caption>
        <thead>
          <tr>
            <th>Month</th>
            {series.map((s) => <th key={s.key}>{s.label}</th>)}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m, i) => (
            <tr key={m}>
              <th>{LONG.format(at(m))}</th>
              {series.map((s) => <td key={s.key}>{kwh(s.values[m] ?? 0)}</td>)}
              <td>{kwh(totals[i]!)}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

/** Made-up bar heights (% of the plot) for the loading shape: varied, so it reads as a chart. */
const SKELETON_BARS = [38, 52, 46, 68, 30, 58, 44, 50, 74, 62, 88, 70, 54];

/**
 * While the numbers load: the chart's shape in grey, the same size and layout as the real one
 * (axis, 13 columns, month labels), so nothing jumps when it's drawn.
 */
function ChartSkeleton() {
  return (
    <Box role="status" aria-label="Loading the chart">
      <Box sx={{ display: 'flex', gap: 1 }} aria-hidden>
        <Box sx={{ width: 56, height: PLOT_H, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="text" width={24} sx={{ fontSize: 12 }} />)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ height: PLOT_H, display: 'flex', alignItems: 'flex-end', borderBottom: `1px solid ${colours.coolGrey}` }}>
            {SKELETON_BARS.map((h, i) => (
              <Box key={i} sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                <Skeleton variant="rectangular" sx={{ width: '60%', maxWidth: 24, height: `${h}%`, borderRadius: '4px 4px 0 0' }} />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', mt: 1 }}>
            {SKELETON_BARS.map((_, i) => (
              <Box key={i} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <Skeleton variant="text" width="50%" sx={{ maxWidth: 28, fontSize: 12, lineHeight: 1.3 }} />
              </Box>
            ))}
          </Box>
          {/* The year line under the month labels. */}
          <Box sx={{ height: 16 }} />
        </Box>
      </Box>
    </Box>
  );
}

function Tip({ month, series, total, side }: { month: string; series: ChartSeries[]; total: number; side: 'left' | 'right' }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '55%',
        ...(side === 'right' ? { left: '70%' } : { right: '70%' }),
        zIndex: 2,
        minWidth: 180,
        p: 1.5,
        bgcolor: colours.white,
        border: `1px solid ${colours.hairline}`,
        borderRadius: '12px', // small panel; no shadow (design guidelines)
        pointerEvents: 'none',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>{LONG.format(at(month))}</Typography>
      {series.map((s) => (
        <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color }} />
            <Typography variant="body2" color="textSecondary">{s.label}</Typography>
          </Box>
          <Typography variant="body2" className="num">{kwh(s.values[month] ?? 0)} kWh</Typography>
        </Box>
      ))}
      {series.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, pt: 0.75, borderTop: `1px solid ${colours.hairline}` }}>
          <Typography variant="body2">Total</Typography>
          <Typography variant="body2" className="num" sx={{ fontWeight: 600 }}>{kwh(total)} kWh</Typography>
        </Box>
      )}
    </Box>
  );
}
